import type { UiLocationKey } from '../components/tour/uiLocations';

/**
 * Change log / “Recently added” for Documentation.
 * Each entry explains what changed, how it was done, why, and how it helps UX.
 * Live version emails remain Settings → What’s New (API-backed).
 */
export interface NewFeatureEntry {
    id: string;
    title: string;
    blurb: string;
    shipped: string;
    /** How the change was implemented (officer-readable, not a git dump) */
    howDone: string;
    /** Why we made the change */
    whyDone: string;
    /** How it helps officer UX */
    uxHelp: string;
    href?: string;
    locate?: UiLocationKey;
}

export const NEW_FEATURES: NewFeatureEntry[] = [
    {
        id: 'fusion-legend-inspector',
        title: 'Fusion Legend + Inspector case overview',
        blurb:
            'Surface chips moved into a Workbench-style Legend; empty Inspector shows case graph stats before you pick a node.',
        shipped: 'Sep 2026',
        howDone:
            'Fusion header dropped the surfaces strip; Legend popup toggles Banking/Crypto/Social/Network/Company/Identity. Inspector empty state lists nodes, links, scored entities, handovers, and traced value.',
        whyDone:
            'Header chrome competed with the canvas; officers needed a glanceable case overview without selecting a synthetic node.',
        uxHelp:
            'Open Legend to show/hide modalities; read case overview in Inspector, then click a node for the dossier.',
        href: '/cases',
    },
    {
        id: 'guest-fusion-landing-demos',
        title: 'Guest Fusion demos from Landing (no SSO)',
        blurb:
            'Partners can open Fusion Canvas demo scenarios from Landing without logging in.',
        shipped: 'Sep 2026',
        howDone:
            'Guest demo sandbox + Landing CTAs load curated Fusion demos without SSO. Live case Workbench and real case Fusion Canvas stay auth-gated.',
        whyDone:
            'Demo walkthroughs stalled on OTP; partners could not see the OSINT→graph story before credentials.',
        uxHelp:
            'Try Fusion on Landing first; when you have a real case, use Cases → Fusion Canvas — not the /fusion sandbox alone.',
        href: '/',
    },
    {
        id: 'file-dropzone-bank-pdf-password',
        title: 'Shared FileDropzone + password bank PDFs',
        blurb:
            'Evidence and statement uploads share one dropzone; Fusion can unlock password-protected bank statement PDFs.',
        shipped: 'Sep 2026',
        howDone:
            'Reusable FileDropzone for evidence/statement paths. StatementFlow accepts a PDF password when the bank export is encrypted.',
        whyDone:
            'FIR evidence and bank PDFs failed at upload or silently skipped encrypted statements — cash-flow join never started.',
        uxHelp:
            'Drag files into the shared dropzone; if a bank PDF asks for a password, enter it once and continue the Fusion bank import.',
        href: '/cases',
    },
    {
        id: 'fusion-canvas-workbench-parity',
        title: 'Fusion Canvas rename + Workbench chrome parity',
        blurb:
            'Case Canvas is Fusion Canvas; layout, filters, and Demo Guide now match Workbench habits.',
        shipped: 'Sep 2026',
        howDone:
            'Product rename across Cases UI. Stage layout, collapsible filters, dotted canvas, and Workbench-style Demo Guide scenarios shipped on Fusion.',
        whyDone:
            'Officers treated /fusion as “the product” and missed the per-case graph; chrome felt like a different app.',
        uxHelp:
            'Always open Cases → your case → Fusion Canvas for real joins. Use filters and Demo Guide the same way you would on Workbench.',
        href: '/cases',
    },
    {
        id: 'case-status-processing',
        title: 'Case status: Processing',
        blurb:
            'Cases can be marked Processing between Open and Closed.',
        shipped: 'Sep 2026',
        howDone:
            'PROCESSING added to the case lifecycle status enum and case UI. Product definitions (who can set, list filters, Closed rules) still tracked with Saurabh.',
        whyDone:
            'Open vs Closed was too coarse for dockets that are actively being worked but not finished.',
        uxHelp:
            'Use Processing for in-progress dockets; confirm team conventions before filtering briefings on status alone.',
        href: '/cases',
    },
    {
        id: 'case-resume-choice',
        title: 'Workbench entry: Resume / Blank / Open saved',
        blurb:
            'Leaving a case mid-investigation no longer dumps you onto an ambiguous canvas.',
        shipped: 'Sep 2026',
        howDone:
            'Case Detail Workbench / New Investigation / Add Address open a choice modal. Session drafts now store case_id and investigation_id. Blank uses fresh=1 to clear leftover graph; Resume uses resume=1 to restore the matching draft.',
        whyDone:
            'Officers returned from OTP login or another tab and could not tell whether the canvas was “theirs,” another case’s leftover graph, or a brand-new blank — wrong briefings and accidental overwrites.',
        uxHelp:
            'You always choose: continue unfinished work, start clean under the same case, or open a named snapshot. Per-row Resume on a saved investigation stays one click.',
        href: '/cases',
    },
    {
        id: 'documentation-hub',
        title: 'In-app Documentation hub',
        blurb:
            'Sidebar Documentation hosts glossary, feature encyclopedia, chain reference, and change notes.',
        shipped: 'Sep 2026',
        howDone:
            'Added /documentation with searchable registries (glossary, features, chains, changelog) wired into sidebar, Help menu, and account menu — same chrome as Feedback/Settings.',
        whyDone:
            'New officers had Demo Guide + tribal Slack knowledge only. Partners asked for product help without reading engineering docs.',
        uxHelp:
            'Stay inside the Workbench shell to learn what a control means, how to use it, and what changed — without a marketing FAQ site.',
        href: '/documentation',
        locate: 'nav-documentation',
    },
    {
        id: 'fusion-phone-merge',
        title: 'Fusion phone merge on Fusion Canvas',
        blurb:
            'Instagram, Telegram, and P2P phones join one case-graph node when they share a canonical phone.',
        shipped: 'Sep 2026',
        howDone:
            'Identifier canon normalizes phone forms (+91 vs 10-digit) on the backend and frontend so harvests merge instead of duplicating nodes.',
        whyDone:
            'Split phones broke Fusion — the same real-world person appeared as two nodes and OSINT never “clicked” onto cryptoflow.',
        uxHelp:
            'One person, one node. Officers stop re-learning three desks that refused to join.',
        href: '/cases',
    },
    {
        id: 'interactive-le-report',
        title: 'Interactive LE Report shell',
        blurb:
            'Printable reports get a table of contents and collapsible sections.',
        shipped: 'Sep 2026',
        howDone:
            'Report HTML scaffolding adds TOC anchors and collapsible appendix blocks while keeping print styles.',
        whyDone:
            'Long reports were a flat scroll — supervisors could not jump to exposure or trace appendices during review.',
        uxHelp:
            'Jump to the section you need; collapse noise; still print for the case file.',
        locate: 'le-report',
    },
    {
        id: 'ig-promote-case-canvas',
        title: 'Instagram promote → Fusion Canvas',
        blurb:
            'Compare-IG promote hands identities into Fusion Canvas, not a dead-end /fusion sandbox.',
        shipped: 'Sep 2026',
        howDone:
            'Promote handoff routes to the case Fusion Canvas path with the selected case context instead of an unlinked demo surface.',
        whyDone:
            'Officers harvested useful IG leads then “promoted” into a surface that did not continue the case — value loop broke.',
        uxHelp:
            'Harvest → promote → case graph stays one story.',
        href: '/instagram-network',
    },
    {
        id: 'data-mode-pill',
        title: 'Mock / Cached / Live data-mode pill',
        blurb:
            'Workbench chrome states whether data is mock, cached, or live.',
        shipped: 'Aug 2026',
        howDone:
            'A persistent pill in Workbench chrome reads the active data mode from session / demo flags and API cache posture.',
        whyDone:
            'Demo and cached responses were being briefed as live chain truth in partner walkthroughs.',
        uxHelp:
            'Glance at the pill before quoting numbers — honesty is part of the product.',
        href: '/workbench',
    },
    {
        id: 'platform-tour-ui-locations',
        title: 'Platform Tour + shared “where is this?” pointers',
        blurb:
            'Tour, Demo Guide, and jargon tooltips share one location registry.',
        shipped: 'Aug 2026',
        howDone:
            'uiLocations.ts holds selector + where + reveal for each control; tour and jargon read the same keys (R30).',
        whyDone:
            'Copy said “click Trace” while Trace lived in three places and gold trails needed a prior run — spotlights failed silently.',
        uxHelp:
            '“Show me” points at a real control or explains the precondition instead of highlighting nothing.',
        href: '/demos',
        locate: 'dashboard-demos',
    },
];
